<template>
    <div class="deadline">
        <div
            class="deadline-set"
            v-if="hasDeadline">
            <div class="deadline-header">
                <h3 class="title">Deadline</h3>
                <b-button
                    class="btn-link-type btn-edit-deadline"
                    v-b-modal="'set-deadline-modal'"
                    v-if="ischiefeditor && canChangeDeadline()"
                    variant="primary">
                    {{ $t('common.edit') }}
                </b-button>
            </div>

            <div class="deadline-date">{{ formatDeadline() }}</div>

            <div class="deadline-date-remaining">
                <div class="days">
                    <div class="number">
                        {{ daysLeft() }}<span>{{ $t('magazine.days') }}</span>
                    </div>
                </div>
                <div class="hours">
                    <div class="number">
                        {{ hoursLeft() }}<span>{{ $t('magazine.hours') }}</span>
                    </div>
                </div>
                <div class="minutes">
                    <div class="number">
                        {{ minutesLeft() }}<span>{{ $t('magazine.minutes') }}</span>
                    </div>
                </div>
            </div>
        </div>
        <div
            class="deadline-not-set"
            v-if="!hasDeadline && ischiefeditor">
            <h4 class="title">{{ $t('magazine.no_deadline_set') }}</h4>
            <b-button
                class="btn-link-type btn-edit-deadline"
                v-b-modal="'set-deadline-modal'"
                v-if="ischiefeditor"
                variant="primary">
                {{ $t('common.edit') }}
            </b-button>
        </div>
        <div>
            <p v-if="magazine.editorial_deadline_date && ischiefeditor">
                {{ $t('magazine.redaction_deadline') }} {{ magazine.editorial_date_time | date_time_om }}.
            </p>
            <p v-if="magazine.delivery_date != null && ischiefeditor">
                {{ $t('magazine.delivery_date') }} {{ magazine.delivery_date | date_named_month_short }}.
            </p>
            <p v-else-if="regulardeadline && magazine.deadline.delivery_date != '' && ischiefeditor">
                {{ $t('magazine.delivery_date') }} {{ magazine.deadline.delivery_date | date_named_month_short }}.
            </p>
        </div>

        <b-modal
            :id="'set-deadline-modal'"
            :ref="'set-deadline-modal'"
            :title="$t('magazine.set_deadline')"
            :ok-title="$t('common.save')"
            :cancel-title="$t('common.cancel')"
            cancel-variant="outline-primary"
            @ok="onOk()"
            modal-class="apprentice-default-modal set-deadline-modal">
            <div
                class="magazine-deadline"
                v-if="isAdmin || ischiefeditor">
                <b-form-group
                    v-if="isAdmin"
                    label-for="deadline-select-input"
                    label="Deadline">
                    <b-form-select
                        class="apprentice-primary-select"
                        id="deadline-select-input"
                        v-model="magazine.deadline_list_item_id"
                        @change="onDateChange">
                        <option
                            v-if="isAdmin"
                            :value="null">
                            {{ $t('magazine.exception') }}
                        </option>
                        <option
                            v-for="deadline in deadlines"
                            :value="deadline.id">
                            {{ deadline.agenda_item.fulldate }}
                        </option>
                    </b-form-select>
                </b-form-group>

                <div
                    class="deadline-exception"
                    v-if="showException">
                    <b-form-group
                        class="deadline-exception-group date-group"
                        :label="$t('magazine.exception_date')"
                        label-for="deadline-exception">
                        <b-form-datepicker
                            class="apprentice-date-picker"
                            id="deadline-exception"
                            v-model="customdeadline"
                            start-weekday="1"
                            now-button
                            reset-button
                            right
                            today-variant="primary"
                            nav-button-variant="primary"
                            locale="nl"
                            :aria-controls="$t('magazine.choose_a_date')"
                            label-reset-button="reset"
                            :label-no-date-selected="$t('magazine.choose_a_date')"
                            menu-class="datedropdown"
                            :date-format-options="{ year: 'numeric', month: 'short', day: '2-digit', weekday: 'short' }"
                            @dp-change="setCustomDeadline">
                        </b-form-datepicker>
                    </b-form-group>

                    <b-form-group
                        class="deadline-time-exception-group time-group"
                        :label="$t('magazine.exception_time')"
                        label-for="deadline-time-exception-input">
                        <design-2021-apprentice-timepicker-component
                            id="deadline-time-exception-input"
                            v-model="time">
                        </design-2021-apprentice-timepicker-component>
                    </b-form-group>
                </div>

                <div class="editorial-deadline">
                    <b-form-group
                        class="date-group"
                        :label="$t('magazine.editorial_date')"
                        label-for="editorial-deadline-input">
                        <b-form-datepicker
                            class="apprentice-date-picker"
                            id="editorial-deadline-input"
                            v-model="editorialDeadlineDate"
                            start-weekday="1"
                            now-button
                            reset-button
                            right
                            today-variant="primary"
                            nav-button-variant="primary"
                            locale="nl"
                            :aria-controls="$t('magazine.choose_a_date')"
                            label-reset-button="reset"
                            :label-no-date-selected="$t('magazine.choose_a_date')"
                            menu-class="datedropdown"
                            :date-format-options="{
                                year: 'numeric',
                                month: 'short',
                                day: '2-digit',
                                weekday: 'short',
                            }">
                        </b-form-datepicker>
                    </b-form-group>

                    <b-form-group
                        class="time-group"
                        :label="$t('magazine.editorial_time')"
                        label-for="editorial-deadline-time-input">
                        <design-2021-apprentice-timepicker-component
                            id="editorial-deadline-time-input"
                            v-model="editorialDeadlineTime">
                        </design-2021-apprentice-timepicker-component>
                    </b-form-group>
                </div>

                <b-form-group
                    v-if="isAdmin"
                    :label="$t('magazine.edited_delivery_date')"
                    label-for="deadline-delivery-date">
                    <b-form-datepicker
                        class="apprentice-date-picker"
                        id="deadline-delivery-date"
                        v-model="customDeliveryDate"
                        start-weekday="1"
                        now-button
                        reset-button
                        right
                        today-variant="primary"
                        nav-button-variant="primary"
                        locale="nl"
                        :aria-controls="$t('magazine.choose_a_date')"
                        label-reset-button="reset"
                        :label-no-date-selected="$t('magazine.choose_a_date')"
                        menu-class="datedropdown"
                        :date-format-options="{ year: 'numeric', month: 'short', day: '2-digit', weekday: 'short' }"
                        @dp-change="setCustomDeadline">
                    </b-form-datepicker>
                </b-form-group>
            </div>
        </b-modal>
    </div>
</template>

<script>
export default {
    name: 'design-2021-magazine-deadline-component',
    props: ['magazineIn', 'deadlinesIn', 'ischiefeditor'],
    data() {
        return {
            datepickerOptionsDate: {
                format: 'DD-MM-YYYY',
                useCurrent: false,
            },
            magazine: this.magazineIn,
            hasDeadline: this.magazineIn.customdeadline !== null || this.magazineIn.deadline !== null,
            isAdmin: !!AuthIsAdmin,
            showException: this.magazineIn.deadline == null && !!AuthIsAdmin,
            regulardeadline: this.magazineIn.deadline == null ? 0 : 1,
            customdeadline: this.magazineIn.customdeadline != null ? this.magazineIn.customdeadline.date : '',
            editorialDeadlineDate:
                this.magazineIn.editorial_deadline_date != null ? this.magazineIn.editorial_deadline_date : '',
            editorialDeadlineTime:
                this.magazineIn.editorial_deadline_time != null ? this.magazineIn.editorial_deadline_time : '',
            time: this.magazineIn.customdeadline != null ? this.magazineIn.customdeadline.time : '',
            deadline_time:
                this.magazineIn.agenda_item != null && this.magazineIn.deadline.agenda_item != null
                    ? this.magazineIn.deadline.agenda_item.time
                    : '',
            customDeliveryDate: this.magazineIn.delivery_date != null ? this.magazineIn.delivery_date : '',
        };
    },
    computed: {
        deadlines() {
            if (AuthIsAdmin) return this.deadlinesIn;

            const today = this.moment();
            return this.deadlinesIn.filter((deadline) => {
                const date = this.moment(deadline.agenda_item.date + ' ' + deadline.agenda_item.time);
                const diff = date.diff(today, 'days');
                return diff >= 10;
            });
        },
    },
    methods: {
        canChangeDeadline() {
            if (this.ischiefeditor) return true;

            if (this.magazine.type_id == 2 && !this.isAdmin) return false;

            if (!this.hasDeadline || this.isAdmin) return true;

            const today = this.moment();
            const date = this.moment(this.getDeadline().date + ' ' + this.getDeadline().time);
            const diff = date.diff(today, 'days');

            return diff >= 10 ? true : false;
        },
        onOk() {
            //check older deadline (or deadline to close) not possible

            this.alert.saving();

            const data = {
                deadline_list_item_id: this.magazine.deadline_list_item_id,
                customdeadline: this.customdeadline,
                editorial_deadline_date: this.editorialDeadlineDate,
                editorial_deadline_time: this.editorialDeadlineTime,
                delivery_date: this.customDeliveryDate,
                custom_time: this.time,
                deadline_time: this.deadline_time,
            };

            this.axios.post(this.routes.magazine(this.magazine.id) + '/deadline', data).then(
                (response) => {
                    this.alert.success();
                    window.location.reload();
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },
        onDateChange(value) {
            if (value == null) this.showException = true;
            else this.showException = false;
        },
        setCustomDeadline(value) {},
        formatDeadline() {
            if (!this.ischiefeditor && this.magazine.editorial_deadline_date) {
                //only show editorial deadline if available
                return this.$options.filters.date_time_om_year(
                    this.magazine.editorial_deadline_date + ' ' + this.magazine.editorial_deadline_time,
                );
            } else {
                if (this.getDeadline().time == '00:00:00' || this.getDeadline().time == null) {
                    return this.$options.filters.date_named_month(this.getDeadline().date + ' ' + '00:00:00');
                } else {
                    return this.$options.filters.date_time_om_year(
                        this.getDeadline().date + ' ' + this.getDeadline().time,
                    );
                }
            }
        },

        getDeadline() {
            if (this.magazine.customdeadline != null) {
                return this.magazine.customdeadline;
            } else {
                return this.magazine.deadline.agenda_item;
            }
        },
        daysLeft() {
            if (!this.hasDeadline) return 0;

            var curdate = this.getDeadline().date;
            var curtime = this.getDeadline().time;

            if (curtime == null) {
                curtime = '00:00:00';
            }

            if (!this.ischiefeditor && this.magazine.editorial_deadline_date) {
                curdate = this.magazine.editorial_deadline_date;
                curtime = this.magazine.editorial_deadline_time;
            }

            const today = this.moment();
            const date = this.moment(curdate + ' ' + curtime);
            const diff = date.diff(today, 'days');
            return diff > 0 ? diff : 0;
        },
        hoursLeft() {
            if (!this.hasDeadline) return 0;

            var curdate = this.getDeadline().date;
            var curtime = this.getDeadline().time;

            if (curtime == null) {
                curtime = '00:00:00';
            }

            if (!this.ischiefeditor && this.magazine.editorial_deadline_date) {
                curdate = this.magazine.editorial_deadline_date;
                curtime = this.magazine.editorial_deadline_time;
            }

            const today = this.moment();
            const date = this.moment(curdate + ' ' + curtime);
            const diff = date.diff(today, 'hours') - 24 * this.daysLeft();
            return diff > 0 ? diff : 0;
        },
        minutesLeft() {
            if (!this.hasDeadline) return 0;

            var curdate = this.getDeadline().date;
            var curtime = this.getDeadline().time;

            if (curtime == null) {
                curtime = '00:00:00';
            }

            if (!this.ischiefeditor && this.magazine.editorial_deadline_date) {
                curdate = this.magazine.editorial_deadline_date;
                curtime = this.magazine.editorial_deadline_time;
            }

            const today = this.moment();
            const date = this.moment(curdate + ' ' + curtime);
            const diff = date.diff(today, 'minutes') - 24 * 60 * this.daysLeft() - 60 * this.hoursLeft();
            return diff > 0 ? diff : 0;
        },
    },
};
</script>

<style scoped lang="scss">
.deadline {
    padding-bottom: map-get($grid-gutter-spacing, 'twenty-one');

    &:not(:last-child) {
        border-bottom: 3px dotted $border-color;
    }
}

.deadline-not-set {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;

    .title {
        margin-bottom: 0;
        margin-right: map-get($grid-gutter-spacing, 'fifteen');
    }
}

.btn-edit-deadline {
    &:active,
    &:focus {
        background-color: transparent !important;
        box-shadow: none !important;
    }
}

.deadline-set {
    .deadline-header {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;

        .title {
            margin-bottom: 0;
            margin-right: map-get($grid-gutter-spacing, 'fifteen');
        }
    }
}

.deadline-date {
    font-size: $base-font-size;
    font-weight: $font-weight-medium;
    margin-bottom: map-get($grid-gutter-spacing, 'five');
}

.deadline-date-remaining {
    display: flex;
    flex-direction: row;
    color: $primary;
    font-size: 1.125rem;
    font-weight: $font-weight-bold;

    div {
        span {
            margin-left: 3px;
        }

        &:not(:last-child) {
            margin-right: map-get($grid-gutter-spacing, 'ten');
        }
    }
}

.magazine-deadline ::v-deep {
    select,
    input,
    .time-group input {
        margin-bottom: 0;
    }
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .deadline-exception,
    .editorial-deadline {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;

        .date-group {
            width: calc(67% - 15px);
        }

        .time-group {
            width: calc(33% - 15px);
        }
    }
}
</style>
